import { httpClient } from "api/httpClient";
import { storagePlacesFilterToQuery } from "components/inputs/_helpers/storagePlaces";
import { envSettings } from "core/EnvSettings";
import { ScannerLogContextType } from "entities/ScannerLogContextType";
import React, { useEffect, useRef, useState } from "react";
import { useGetOne, useNotify, useTranslate } from "react-admin";
import { toast } from "react-toastify";
import { useNotifyError } from "utils/notifiers/useNotifyError";
import { matchPalletByScannedSscc, PALLET_SCAN_MATCH } from "utils/scanner/matchPalletByScannedSscc";
import { useGS1BarcodeResolver } from "utils/scanner/useGS1BarcodeResolver";

import { operationModeToggleEnum } from "../../PickingList/components/OperationModeToggleFilter";
import { ValidationState } from "../components/ScanSlots";
import { useOrderServiceType } from "./useOrderServiceType";

const ERROR_FLASH_DURATION_MS = 1200;

export const useScannerOperations = ({
	record,
	operationMode,
	palletsBaseFilter,
	allowStorePallet,
	isLoadingMode,
	isMoveToPreparationMode,
	isScanToCreateActive,
	recordPrepPlaceId,
	setSelectedPalletsForLoading,
	registerScanHandler,
	registerTargetPage,
	isConnected,
	isManualAuthorizationRef,
	setScanModifiedPalletIds,
	setStoragePlaces,
	refetch,
}) => {
	const translate = useTranslate();
	const notify = useNotify();
	const notifyError = useNotifyError();

	const { tryScan, isResolving } = useGS1BarcodeResolver();

	const { isOutgoingPlukk, isCSOrIncomingPlukk, isCrossDock } = useOrderServiceType(record);

	const isSelectionMode = isLoadingMode || isMoveToPreparationMode;
	// Cross-dock scans behave like arrival (CS/incoming plukk) outside selection mode
	// and like outgoing plukk while selecting pallets for preparation/loading.
	const isArrivalScannerMode =
		(isCSOrIncomingPlukk || (isCrossDock && !isSelectionMode)) &&
		(envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE ||
			operationMode === operationModeToggleEnum[0].value);
	const isPlukkScannerMode =
		(isOutgoingPlukk || (isCrossDock && isSelectionMode)) &&
		(envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE
			? isSelectionMode
			: operationMode === operationModeToggleEnum[0].value);
	const isScannerMode = isArrivalScannerMode || isPlukkScannerMode;
	// scan-to-create is scanner-driven too, so it must register the page / connect like scanner mode.
	const isScannerActive = isScannerMode || isScanToCreateActive;

	// Scanner slot states
	const [scannedPallet, setScannedPallet] = useState(null);
	const [scannedStorage, setScannedStorage] = useState(null);
	const [palletValidationState, setPalletValidationState] = useState(ValidationState.neutral);
	const [storageValidationState, setStorageValidationState] = useState(ValidationState.neutral);
	const [suggestedStoragePlace, setSuggestedStoragePlace] = useState(null);
	const [previousWarningStorage, setPreviousWarningStorage] = useState(null);
	const [lastScannerMessage, setLastScannerMessage] = useState(null);
	const [isScannerLoading, setIsScannerLoading] = useState(false);

	// Pallets stacking: extra pallet records stacked onto the scanned one (share one capacity slot)
	const [stackedPallets, setStackedPallets] = useState([]);
	const [pendingStackPallet, setPendingStackPallet] = useState(null);
	// Join flow: scanned place already holds a stack the pallet may join
	const [pendingJoinStack, setPendingJoinStack] = useState(null);

	const { data: terminalData } = useGetOne("terminal", record?.terminal_id, {
		enabled: isArrivalScannerMode && !!record?.terminal_id,
	});
	const isStackingEnabled =
		envSettings.REACT_APP_FF_STORAGE_MODULE && !!terminalData?.is_pallet_stacking_allowed;

	// Drop the held pallet and any pending stack questions — after a successful store
	// the next scan must start clean, or it would offer to stack onto a stored pallet
	const resetHeldPallet = React.useCallback(() => {
		setScannedPallet(null);
		setStackedPallets([]);
		setPendingStackPallet(null);
		setPendingJoinStack(null);
	}, []);

	const flashPalletError = React.useCallback((message) => {
		setLastScannerMessage(message);
		setPalletValidationState(ValidationState.error);
		setTimeout(() => setPalletValidationState(ValidationState.neutral), ERROR_FLASH_DURATION_MS);
	}, []);

	const prevConnectedRef = useRef(isConnected);

	// Monitor connection state changes
	useEffect(() => {
		const autoHideDuration = 10000;

		if (
			isScannerActive &&
			isConnected &&
			!prevConnectedRef.current &&
			!isManualAuthorizationRef.current
		) {
			notify(`✅ ${translate("scanner.notifications.connected")}`, {
				type: "success",
				autoHideDuration,
			});
		}

		// Always track latest connection state so re-entering scanner mode can't fire a stale toast.
		prevConnectedRef.current = isConnected;
	}, [isConnected, notify, translate, isManualAuthorizationRef, isScannerActive]);

	const handleScanStore = React.useCallback(
		(palletToStore = null, storageToUse = null, joinStackGroupId = null) => {
			const pallet = palletToStore || scannedPallet?.data;
			const storage = storageToUse || scannedStorage?.data;

			if (!pallet || !storage) {
				console.error("handleScanStore: missing pallet or storage", { pallet, storage });
				return;
			}

			const palletsToStore = [pallet, ...stackedPallets.map((p) => p.data)];
			// Atomic batch endpoint for stacks (new stack or joining an existing one);
			// plain single store keeps the original PATCH
			const isStackStore = palletsToStore.length > 1 || !!joinStackGroupId;

			const request = isStackStore
				? httpClient("store-stacked-pallets", {
						method: "POST",
						body: JSON.stringify({
							pallet_ids: palletsToStore.map((p) => p.id),
							storage_place_id: storage.id,
							...(joinStackGroupId && { stack_group_id: joinStackGroupId }),
						}),
				  }).then(({ json }) => json.pallet_ids)
				: httpClient(`pallets/${pallet.id}`, {
						method: "PATCH",
						body: JSON.stringify(
							envSettings.REACT_APP_FF_STORAGE_MODULE
								? { storage_place_id: storage.id }
								: { area_id: storage.id }
						),
				  }).then(({ json }) => [json.id]);

			setIsScannerLoading(true);
			request
				.then((storedPalletIds) => {
					// Update storage places map if using storage module
					if (envSettings.REACT_APP_FF_STORAGE_MODULE && storage) {
						setStoragePlaces((prev) => ({
							...prev,
							[storage.id]: storage,
						}));
					}

					// Track these pallets as modified by scanner
					setScanModifiedPalletIds((prev) => [
						...prev,
						...storedPalletIds.filter((id) => !prev.includes(id)),
					]);

					setScannedStorage((prev) => ({ ...prev, isAccepted: false }));
					resetHeldPallet();
					setPalletValidationState(ValidationState.success);
					setStorageValidationState(ValidationState.neutral);
					setSuggestedStoragePlace(null);
					setPreviousWarningStorage(null);
					setLastScannerMessage({
						type: "success",
						text: translate("tab.pallets.pallet_scan_store_success"),
					});
					refetch();
				})
				.catch((err) => {
					notifyError(err);
					// Reset validation states to show error on slots
					setStorageValidationState(ValidationState.error);
					setLastScannerMessage({
						type: "error",
						text: err?.body?.message || err?.message || translate("tab.pallets.error_store_pallet"),
					});
					// Reset to neutral after animation
					setTimeout(() => {
						setStorageValidationState(ValidationState.neutral);
					}, 1200);
				})
				.finally(() => {
					setIsScannerLoading(false);
				});
		},
		[scannedPallet, scannedStorage, stackedPallets, resetHeldPallet, notifyError, translate]
	);

	// Final step of a place scan: when a single pallet lands on a place that already
	// holds exactly one stack, ask whether it joins that stack; otherwise store directly
	const completeStore = React.useCallback(
		(pallet, storage) => {
			const canJoinPlaceStack =
				isStackingEnabled &&
				stackedPallets.length === 0 &&
				storage?.stack_group_ids?.length === 1 &&
				pallet?.stack_group_id !== storage.stack_group_ids[0];
			if (canJoinPlaceStack) {
				setPendingJoinStack({ pallet, storage, stackGroupId: storage.stack_group_ids[0] });
				return;
			}
			handleScanStore(pallet, storage);
		},
		[isStackingEnabled, stackedPallets, handleScanStore]
	);

	// YES joins the held stack at the scanned place, NO stores the pallet on its own
	const resolveJoinStack = React.useCallback(
		(shouldJoin) => {
			if (!pendingJoinStack) return;
			const { pallet, storage, stackGroupId } = pendingJoinStack;
			setPendingJoinStack(null);
			handleScanStore(pallet, storage, shouldJoin ? stackGroupId : null);
		},
		[pendingJoinStack, handleScanStore]
	);

	const handlePlukkScan = React.useCallback(
		(code) => {
			const started = tryScan(code, async ({ result, error }) => {
				toast.info(translate("scanner.notifications.scanned_success", { code }), {
					autoClose: 2000,
				});

				if (error) {
					if (error?.status === 400) {
						notify(translate("tab.pallets.error_scan_entity"), { type: "warning" });
					} else {
						notifyError(error);
					}
					return;
				}
				const sscc = result?.sscc ?? null;
				if (!sscc) {
					notify(translate("tab.pallets.error_scan_entity"), { type: "warning" });
					return;
				}

				const filterPayload = {
					...palletsBaseFilter,
					scanned_sscc: sscc,
					no_range: true,
				};
				const filterQueryString = encodeURIComponent(JSON.stringify(filterPayload));

				let json;
				try {
					// await keeps the fetch inside the single-flight lock
					({ json } = await httpClient(`pallets?filter=${filterQueryString}`));
				} catch (err) {
					notifyError(err);
					return;
				}

				const match = matchPalletByScannedSscc(json ?? [], sscc);
				if (match.status === PALLET_SCAN_MATCH.AMBIGUOUS) {
					notify(translate("scanner.pallet_sscc_ambiguous"), { type: "warning" });
					return;
				}
				if (match.status !== PALLET_SCAN_MATCH.MATCHED) {
					notify(translate("tab.pallets.error_scanned_pallet_fetch"), { type: "error" });
					return;
				}

				const { pallet } = match;
				if (pallet.loaded_into_truck) {
					notify(translate("tab.pallets.pallet_already_loaded"), { type: "warning" });
					return;
				}

				if (!isSelectionMode) {
					notify(translate("tab.pallets.enable_loading_mode_first"), { type: "warning" });
					return;
				}

				if (!pallet.is_storage_place_confirmed) {
					if (isLoadingMode || !pallet.is_target) {
						notify(translate("tab.pallets.error_storage_place_not_confirmed"), {
							type: "warning",
						});
						return;
					}
				}

				if (isMoveToPreparationMode && pallet.into_preparation_place) {
					notify(translate("tab.pallets.error_pallet_already_in_preparation"), {
						type: "warning",
					});
					return;
				}

				if (isMoveToPreparationMode && !recordPrepPlaceId) {
					notify(translate("tab.pallets.error_preparation_place_required"), { type: "warning" });
					return;
				}

				setSelectedPalletsForLoading((prev) => {
					if (prev.includes(pallet.id)) {
						notify(translate("tab.pallets.pallet_unchecked_from_loading"), { type: "info" });
						return prev.filter((id) => id !== pallet.id);
					}

					notify(translate("tab.pallets.pallet_added_to_loading"), { type: "success" });
					return [...prev, pallet.id];
				});
			});
			if (!started) notify(translate("scanner.already_resolving"), { type: "warning" });
		},
		[
			tryScan,
			translate,
			notify,
			palletsBaseFilter,
			isSelectionMode,
			isLoadingMode,
			isMoveToPreparationMode,
			recordPrepPlaceId,
			notifyError,
			setSelectedPalletsForLoading,
			setScanModifiedPalletIds,
			setStoragePlaces,
			refetch,
		]
	);

	// Pallet scan: fetch the pallet by resolved SSCC and validate it for storing.
	// Returns the request promise so callers can hold the single-flight lock until done.
	const runPalletScan = React.useCallback(
		(sscc) => {
			// While a pallet (free or stored) is held and stacking is enabled, keep it in
			// the slot: the scanned pallet may join it as a stacked record instead of replacing it
			const isStackCandidate =
				isStackingEnabled && scannedPallet?.isAccepted && !!scannedPallet?.data;
			if (!isStackCandidate) {
				setScannedPallet({ code: sscc, isAccepted: false });
			}
			const filterQueryString = encodeURIComponent(
				JSON.stringify({
					...palletsBaseFilter,
					scanned_sscc: sscc,
					no_range: true,
				})
			);
			setIsScannerLoading(true);
			return httpClient(`pallets?filter=${filterQueryString}`)
				.then(({ json }) => {
					const match = matchPalletByScannedSscc(json ?? [], sscc);
					if (match.status === PALLET_SCAN_MATCH.AMBIGUOUS) {
						flashPalletError({ type: "warning", text: translate("scanner.pallet_sscc_ambiguous") });
						return;
					}
					if (match.status !== PALLET_SCAN_MATCH.MATCHED) {
						flashPalletError({ type: "error", text: translate("tab.pallets.error_scanned_pallet_fetch") });
						return;
					}
					const { pallet } = match;
					const askToStack = () => {
						const isAlreadyScanned =
							pallet.id === scannedPallet.data.id ||
							stackedPallets.some((p) => p.data.id === pallet.id);
						if (isAlreadyScanned) {
							flashPalletError({ type: "warning", text: translate("tab.pallets.pallet_already_scanned") });
							return;
						}
						setPendingStackPallet({ code: sscc, data: pallet });
					};
					// Validation: check if pallet is not already stored
					const isAlreadyStored = pallet.is_storage_place_confirmed || pallet.area_id;
					if (isAlreadyStored) {
						// A stored pallet can be stacked too (its record will be moved to the
						// stack's place); outside stacking it replaces the held pallet as before
						if (isStackCandidate) {
							askToStack();
							return;
						}
						// Accept already stored pallet without double scan
						setScannedPallet({ code: sscc, data: pallet, isAccepted: true });
						setStackedPallets([]);
						setPalletValidationState(ValidationState.success);
						setLastScannerMessage({
							type: "success",
							text: translate("tab.pallets.pallet_scanned_success"),
						});
						// Clear storage when new pallet is scanned
						setScannedStorage(null);
						setStorageValidationState(ValidationState.neutral);
						setPreviousWarningStorage(null);
						setSuggestedStoragePlace(null);
						return;
					}
					// Validation: check if pallet can be stored (arrived_datetime === null)
					if (!allowStorePallet(pallet)) {
						flashPalletError({ type: "error", text: translate("tab.pallets.error_pallet_not_allowed") });
						return;
					}
					// Stacking: a second pallet scanned while one is held asks
					// whether both records are on one physical pallet
					if (isStackCandidate) {
						askToStack();
						return;
					}
					// Success: pallet is valid
					setScannedPallet({ code: sscc, data: pallet, isAccepted: true });
					setStackedPallets([]);
					setPalletValidationState(ValidationState.success);
					setLastScannerMessage({
						type: "success",
						text: translate("tab.pallets.pallet_scanned_success"),
					});

					// Clear storage when new pallet is scanned
					setScannedStorage(null);
					setStorageValidationState(ValidationState.neutral);
					setPreviousWarningStorage(null);

					// Show suggested storage place
					if (envSettings.REACT_APP_FF_STORAGE_MODULE && pallet.suggested_storage_place_address) {
						setSuggestedStoragePlace(pallet.suggested_storage_place_address);
					} else if (pallet.suggested_area_id) {
						setSuggestedStoragePlace(`S${pallet.suggested_area_id}`);
					}
				})
				.catch((err) => {
					flashPalletError({ type: "error", text: err?.message || translate("tab.pallets.error_scanned_pallet_fetch") });
				})
				.finally(() => {
					setIsScannerLoading(false);
				});
		},
		[palletsBaseFilter, allowStorePallet, translate, isStackingEnabled, scannedPallet, stackedPallets, flashPalletError]
	);

	// "Multiple records on one pallet?" — YES: stack the pallet with the held one
	const confirmStackPallet = React.useCallback(() => {
		if (!pendingStackPallet) return;
		setStackedPallets((prev) => [...prev, pendingStackPallet]);
		setPalletValidationState(ValidationState.success);
		setLastScannerMessage({
			type: "success",
			text: translate("tab.pallets.pallet_added_to_stack"),
		});
		setPendingStackPallet(null);
	}, [pendingStackPallet, translate]);

	// NO: the new pallet replaces the held one (current single-pallet behavior)
	const rejectStackPallet = React.useCallback(() => {
		if (!pendingStackPallet) return;
		const { code, data } = pendingStackPallet;
		setScannedPallet({ code, data, isAccepted: true });
		setStackedPallets([]);
		setPalletValidationState(ValidationState.success);
		setLastScannerMessage({
			type: "success",
			text: translate("tab.pallets.pallet_scanned_success"),
		});
		setScannedStorage(null);
		setStorageValidationState(ValidationState.neutral);
		setPreviousWarningStorage(null);
		if (envSettings.REACT_APP_FF_STORAGE_MODULE && data.suggested_storage_place_address) {
			setSuggestedStoragePlace(data.suggested_storage_place_address);
		} else if (data.suggested_area_id) {
			setSuggestedStoragePlace(`S${data.suggested_area_id}`);
		} else {
			setSuggestedStoragePlace(null);
		}
		setPendingStackPallet(null);
	}, [pendingStackPallet, translate]);

	// Storage/area scan: look up the storage place (or area) and, when it matches the
	// scanned pallet, autocomplete the store. Returns the request promise (or undefined
	// when a pallet must be scanned first) so callers can hold the single-flight lock.
	// Resolves a scanned code to a storage place; falls back to an area when the storage module is off.
	const lookupStoragePlace = React.useCallback(
		async (code) => {
			let resource = "storage-places-lookup";
			let resolvedCode = code;
			let filterPayload = {
				terminal_id: record.terminal_id,
				no_range: true,
				include_virtual: false,
				...storagePlacesFilterToQuery(resolvedCode),
			};
			if (!envSettings.REACT_APP_FF_STORAGE_MODULE) {
				resource = "areas";
				resolvedCode = Number(code.slice(1));
				filterPayload = { id: resolvedCode, no_range: true };
			}
			const filterQueryString = encodeURIComponent(JSON.stringify(filterPayload));
			const { json } = await httpClient(`${resource}?filter=${filterQueryString}`);
			return { place: json?.[0] ?? null, code: resolvedCode };
		},
		[record?.terminal_id]
	);

	const runStorageScan = React.useCallback(
		(code) => {
			setScannedStorage({ code, data: null, isAccepted: false });
			// Storage place scan - only allowed after pallet is scanned
			if (!scannedPallet?.isAccepted) {
				setLastScannerMessage({
					type: "warning",
					text: translate("tab.pallets.error_scan_pallet_first"),
				});
				setStorageValidationState(ValidationState.error);
				setTimeout(() => setStorageValidationState(ValidationState.neutral), 1200);
				return;
			}

			setIsScannerLoading(true);
			return lookupStoragePlace(code)
				.then(({ place: storage, code: _code }) => {
					if (storage) {
						const currentPallet = scannedPallet?.data;

						// Second-level validation: check if storage is recommended for current or another pallet
						const storageIdToCheck = storage.id;
						const currentPalletStorageId = currentPallet?.storage_place_id;

						// Check if the current pallet is already stored somewhere else
						const isCurrentPalletAlreadyStored = currentPallet?.is_storage_place_confirmed;

						// Storing a group that contains already-stored records moves them,
						// so it needs the same rescan confirmation as a single re-store
						const groupHasStoredPallet =
							isCurrentPalletAlreadyStored ||
							stackedPallets.some((p) => p.data.is_storage_place_confirmed);

						// Check if this storage matches the current pallet's recommended storage
						if (currentPalletStorageId === storageIdToCheck && !groupHasStoredPallet) {
							// Perfect match - autocomplete immediately
							setScannedStorage({ code: _code, data: storage, isAccepted: true });
							setStorageValidationState(ValidationState.success);
							setPreviousWarningStorage(null);
							setLastScannerMessage({
								type: "success",
								text: translate("tab.pallets.storage_scanned_success"),
							});

							// Trigger autocomplete with explicit pallet and storage
							setTimeout(() => {
								completeStore(currentPallet, storage);
							}, 300);
							return;
						}

						//TODO - NEED HANDLE
						// Check if this storage is recommended for another pallet in this order
						// const isRecommendedForOtherPallet = pallets.some((p) => {
						// 	return (
						// 		p.id !== currentPallet?.id &&
						// 		p.storage_place_id === storageIdToCheck &&
						// 		!p.is_storage_place_confirmed
						// 	);
						// });

						// Check if pallet has recommended storage but not yet confirmed (and scanned different storage)
						const hasRecommendedButNotConfirmed =
							currentPalletStorageId && !isCurrentPalletAlreadyStored;

						if (groupHasStoredPallet || hasRecommendedButNotConfirmed) {
							// Check if this is a second scan (confirmation)
							if (previousWarningStorage && previousWarningStorage.id === storageIdToCheck) {
								// Second scan of the same storage - confirm and autocomplete
								setScannedStorage({ code: _code, data: storage, isAccepted: true });
								setStorageValidationState(ValidationState.success);
								setPreviousWarningStorage(null);
								setLastScannerMessage({
									type: "success",
									text: translate("tab.pallets.storage_scanned_success"),
								});

								// Trigger autocomplete with explicit pallet and storage
								setTimeout(() => {
									completeStore(currentPallet, storage);
								}, 300);
							} else {
								// First scan or different storage - show warning with error animation
								setScannedStorage({ code: _code, data: storage, isAccepted: false });
								setStorageValidationState(ValidationState.error);
								setPreviousWarningStorage(storage);
								const warningKey = groupHasStoredPallet
									? "tab.pallets.warning_pallet_already_stored"
									: "tab.pallets.warning_storage_for_other_pallet";
								setLastScannerMessage({
									type: "warning",
									text: translate(warningKey),
								});
								setTimeout(() => setStorageValidationState(ValidationState.neutral), 1200);
							}
							return;
						}

						// No recommendation match - still valid but autocomplete
						setScannedStorage({ code: _code, data: storage, isAccepted: true });
						setStorageValidationState(ValidationState.success);
						setPreviousWarningStorage(null);
						setLastScannerMessage({
							type: "success",
							text: translate("tab.pallets.storage_scanned_success"),
						});

						// Trigger autocomplete with explicit pallet and storage
						setTimeout(() => {
							completeStore(currentPallet, storage);
						}, 300);
					} else {
						setLastScannerMessage({
							type: "error",
							text: translate("tab.pallets.error_scanned_storage_fetch"),
						});
						setStorageValidationState(ValidationState.error);
						setTimeout(() => setStorageValidationState(ValidationState.neutral), 1200);
					}
				})
				.catch((err) => {
					setLastScannerMessage({
						type: "error",
						text: err?.message || translate("tab.pallets.error_scanned_storage_fetch"),
					});
					setStorageValidationState(ValidationState.error);
					setTimeout(() => setStorageValidationState(ValidationState.neutral), 1200);
				})
				.finally(() => {
					setIsScannerLoading(false);
				});
		},
		[scannedPallet, stackedPallets, previousWarningStorage, completeStore, translate, lookupStoragePlace]
	);

	// SCAN-TO-CREATE: scanning the SSCC only holds the pallet; scanning the storage place
	// then creates it and stores it there in a single /scan-to-create-pallet request
	// (replaces the previous create POST + store PATCH).
	const handleScanToCreate = React.useCallback(
		(code) => {
			const started = tryScan(code, async ({ result, error }) => {
				toast.info(translate("scanner.notifications.scanned_success", { code }), {
					autoClose: 2000,
				});
				// 400 = not a GS1 code → treat as a storage place; surface real errors.
				if (error && error?.status !== 400) {
					flashPalletError({ type: "error", text: error?.message || translate("tab.pallets.error_scanned_pallet_fetch") });
					return;
				}

				const sscc = result?.sscc ?? null;
				if (sscc) {
					// SSCC scanned → hold it; creation happens with the place scan below.
					setScannedPallet({ code: sscc, isAccepted: true });
					setScannedStorage(null);
					setStorageValidationState(ValidationState.neutral);
					setPalletValidationState(ValidationState.success);
					setLastScannerMessage({
						type: "success",
						text: translate("tab.pallets.pallet_scanned_success"),
					});
					return;
				}

				// Storage place scan — requires a held SSCC.
				if (!scannedPallet?.code) {
					setLastScannerMessage({
						type: "warning",
						text: translate("tab.pallets.error_scan_pallet_first"),
					});
					setStorageValidationState(ValidationState.error);
					setTimeout(() => setStorageValidationState(ValidationState.neutral), 1200);
					return;
				}

				setScannedStorage({ code, data: null, isAccepted: false });

				setIsScannerLoading(true);
				try {
					const { place, code: _code } = await lookupStoragePlace(code);
					if (!place) {
						setLastScannerMessage({
							type: "error",
							text: translate("tab.pallets.error_scanned_storage_fetch"),
						});
						setStorageValidationState(ValidationState.error);
						setTimeout(() => setStorageValidationState(ValidationState.neutral), 1200);
						return;
					}

					// One request: create the pallet and store it at the scanned place.
					const { json: created } = await httpClient("scan-to-create-pallet", {
						method: "POST",
						body: JSON.stringify({
							order_id: record.id,
							sscc: scannedPallet.code,
							storage_place_id: place.id,
						}),
					});
					setScannedPallet({ code: scannedPallet.code, data: created, isAccepted: true });
					setScannedStorage({ code: _code, data: place, isAccepted: true });
					setStorageValidationState(ValidationState.success);
					setLastScannerMessage({
						type: "success",
						text: translate("tab.pallets.pallet_scan_store_success"),
					});
					// Highlight the new pallet row in the grid, like the store-by-scan flow.
					setScanModifiedPalletIds((prev) =>
						prev.includes(created.id) ? prev : [...prev, created.id]
					);
					refetch();
				} catch (err) {
					notifyError(err);
					setStorageValidationState(ValidationState.error);
					setLastScannerMessage({
						type: "error",
						text:
							err?.body?.message ||
							err?.message ||
							translate("tab.pallets.error_scanned_storage_fetch"),
					});
					setTimeout(() => setStorageValidationState(ValidationState.neutral), 1200);
				} finally {
					setIsScannerLoading(false);
				}
			});
			if (!started) notify(translate("scanner.already_resolving"), { type: "warning" });
		},
		[
			tryScan,
			translate,
			notify,
			record,
			notifyError,
			scannedPallet,
			refetch,
			lookupStoragePlace,
			setScanModifiedPalletIds,
			flashPalletError,
		]
	);

	const handleOnScan = React.useCallback(
		(code) => {
			// Stacking/join question must be answered before the next scan
			if (pendingStackPallet || pendingJoinStack) {
				notify(translate("tab.pallets.stack_confirmation_pending"), { type: "warning" });
				return;
			}

			if (isScanToCreateActive) {
				handleScanToCreate(code);
				return;
			}

			if (isOutgoingPlukk || (isCrossDock && isSelectionMode)) {
				if (!isSelectionMode) {
					notify(translate("tab.pallets.enable_loading_mode_first"), { type: "warning" });
					return;
				}
				handlePlukkScan(code);
				return;
			}

			// `S<area>` codes are internal location codes (not GS1) — pre-check before
			// tryScan; they go straight to the storage lookup with no resolve round-trip.
			const isAreaCode = /^S\d+$/.test(code);
			if (isAreaCode) {
				toast.info(translate("scanner.notifications.scanned_success", { code }), {
					autoClose: 2000,
				});
				runStorageScan(code);
				return;
			}

			const started = tryScan(code, async ({ result, error }) => {
				toast.info(translate("scanner.notifications.scanned_success", { code }), {
					autoClose: 2000,
				});
				// 400 = not a GS1 code → not a pallet (fall through to storage); surface real errors.
				if (error && error?.status !== 400) {
					flashPalletError({ type: "error", text: error?.message || translate("tab.pallets.error_scanned_pallet_fetch") });
					return;
				}
				const sscc = result?.sscc ?? null;
				const isPalletCode = !!sscc;

				if (!isPalletCode && !envSettings.REACT_APP_FF_STORAGE_MODULE) {
					flashPalletError({ type: "warning", text: translate("tab.pallets.error_scan_entity") });
					return;
				}

				// await keeps the fetch + autocomplete inside the single-flight lock
				if (isPalletCode) {
					await runPalletScan(sscc);
				} else {
					await runStorageScan(code);
				}
			});
			if (!started) notify(translate("scanner.already_resolving"), { type: "warning" });
		},
		[
			tryScan,
			isScanToCreateActive,
			handleScanToCreate,
			isOutgoingPlukk,
			isCrossDock,
			isSelectionMode,
			handlePlukkScan,
			runPalletScan,
			runStorageScan,
			pendingStackPallet,
			pendingJoinStack,
			translate,
			notify,
			flashPalletError,
		]
	);

	// Clear all scanner state when mode changes
	const clearScannerState = React.useCallback(() => {
		resetHeldPallet();
		setScannedStorage(null);
		setPalletValidationState(ValidationState.neutral);
		setStorageValidationState(ValidationState.neutral);
		setSuggestedStoragePlace(null);
		setPreviousWarningStorage(null);
		setLastScannerMessage(null);
	}, [resetHeldPallet]);

	useEffect(() => {
		clearScannerState();
	}, [clearScannerState, isScannerMode, operationMode, isScanToCreateActive]);

	// Register this page as a target page when scanner or scan-to-create is active
	useEffect(() => {
		if (isScannerActive) {
			const unregisterPage = registerTargetPage("pallets-tab", {
				contextType: ScannerLogContextType.order,
				contextId: record.id,
				terminalId: record.terminal_id,
			});
			return unregisterPage;
		}
	}, [isScannerActive, registerTargetPage, record.id, record.terminal_id]);

	// Register scan handler for this page when scanner or scan-to-create is active
	useEffect(() => {
		if (isScannerActive) {
			const unregister = registerScanHandler("pallets-tab", handleOnScan);
			return unregister;
		}
	}, [isScannerActive, registerScanHandler, handleOnScan]);

	return {
		isPlukkScannerMode,
		isScannerMode,
		scannedPallet,
		scannedStorage,
		stacking: {
			pallets: stackedPallets,
			pending: pendingStackPallet,
			confirm: confirmStackPallet,
			reject: rejectStackPallet,
		},
		joinStack: {
			pending: pendingJoinStack,
			confirm: () => resolveJoinStack(true),
			reject: () => resolveJoinStack(false),
		},
		palletValidationState,
		storageValidationState,
		suggestedStoragePlace,
		lastScannerMessage,
		isScannerLoading: isScannerLoading || isResolving,
		clearScannerState,
	};
};
